import { AloftPrice } from '@aloft/toolkit-shared';
import { Block, Button, Flex, Icon, Typography, colors } from '@aloft/toolkit-sui';

import { CompTypeBadge } from '../../CompTypeBadge.tsx';

type ContextActionBarProps = {
  selectedCount: number;
  showPriceStats: boolean;
  aivAvgSalePrice?: AloftPrice;
  arvAvgSalePrice?: AloftPrice;
  priceDifference?: AloftPrice;
  glaBracketed: boolean;
  siteBracketed: boolean;
  ageBracketed: boolean;
  updateLabel: string;
  onUpdate: () => void;
  onDeselectAll: () => void;
};

const BracketingIndicator = ({ bracketed, label }: { bracketed: boolean; label: string }) => (
  <Flex alignItems="center" gap="4px" flexShrink={0}>
    <Icon icon={bracketed ? 'check' : 'close'} size="md" color={bracketed ? 'success' : 'error'} />
    <Typography variant="body1" color="textPrimary">
      {label}
    </Typography>
  </Flex>
);

const formatPrice = (price?: AloftPrice) => price?.toString(true) ?? '—';

const Divider = () => (
  <Block width="1px" height="24px" backgroundColor="strokeDefault" flexShrink={0} />
);

const actionBarButtonStyles = `
  .recommended-comps-action-bar .button.sm {
    font-size: 0.875rem;
  }
  .recommended-comps-action-bar .button.primary.sm {
    padding: 8px 12px;
    height: auto;
  }
  .recommended-comps-action-bar .button svg.icon {
    width: 16px;
    height: 16px;
  }
  .recommended-comps-action-bar .button.inlineSecondary:not(:disabled) {
    color: ${colors.textLight};
  }
  .recommended-comps-action-bar .button.inlineSecondary:not(:disabled):hover {
    color: ${colors.textPrimary};
    text-underline-offset: 2px;
  }
`;

export const ContextActionBar = ({
  selectedCount,
  showPriceStats,
  aivAvgSalePrice,
  arvAvgSalePrice,
  priceDifference,
  glaBracketed,
  siteBracketed,
  ageBracketed,
  updateLabel,
  onUpdate,
  onDeselectAll,
}: ContextActionBarProps) => {
  if (selectedCount < 1) return null;

  return (
    <Flex
      position="absolute"
      bottom="24px"
      left="0"
      right="0"
      justifyContent="center"
      zIndex="2"
      __dangerDoNotUseSx={{ pointerEvents: 'none' }}
    >
      <style>{actionBarButtonStyles}</style>
      <Flex
        className="recommended-comps-action-bar"
        alignItems="center"
        flexWrap="nowrap"
        gap="16px"
        paddingY="12px"
        paddingX="16px"
        backgroundColor="selected4"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="buttonDefault"
        borderRadius="8px"
        boxShadow="0px 0px 8px 0px rgba(0,0,0,0.08), 0px 8px 16px 0px rgba(0,0,0,0.16)"
        whiteSpace="nowrap"
        __dangerDoNotUseSx={{ pointerEvents: 'auto' }}
      >
        <Flex alignItems="center" gap="4px" flexShrink={0}>
          <Flex
            alignItems="center"
            justifyContent="center"
            padding="4px 8px"
            backgroundColor="surfaceAccent5"
            borderRadius="4px"
          >
            <Typography variant="h4" color="textPrimary">
              {selectedCount}
            </Typography>
          </Flex>
          <Typography variant="body1" color="textPrimary">
            selected
          </Typography>
        </Flex>

        <BracketingIndicator bracketed={glaBracketed} label="Brackets GLA" />
        <BracketingIndicator bracketed={siteBracketed} label="Brackets Site" />
        <BracketingIndicator bracketed={ageBracketed} label="Brackets Age" />

        {showPriceStats && (
          <>
            <Flex alignItems="center" gap="4px" flexShrink={0}>
              <CompTypeBadge compType="aiv" />
              <Typography variant="body1" color="textPrimary">
                Avg: {formatPrice(aivAvgSalePrice)}
              </Typography>
            </Flex>
            <Flex alignItems="center" gap="4px" flexShrink={0}>
              <CompTypeBadge compType="arv" />
              <Typography variant="body1" color="textPrimary">
                Avg: {formatPrice(arvAvgSalePrice)}
              </Typography>
            </Flex>
            <Block flexShrink={0}>
              <Typography variant="body1" color="textPrimary">
                Difference: {formatPrice(priceDifference)}
              </Typography>
            </Block>
          </>
        )}

        <Divider />
        <Block flexShrink={0}>
          <Button text={updateLabel} size="sm" icon="update" onClick={onUpdate} />
        </Block>
        <Divider />
        <Block flexShrink={0}>
          <Button
            text="Deselect all"
            size="sm"
            variant="inlineSecondary"
            icon="information-close"
            onClick={onDeselectAll}
          />
        </Block>
      </Flex>
    </Flex>
  );
};
